import type {
  Collect,
  CollectNotice,
  CollectOpen,
  VendorTicketCodeView,
} from "@libs/platform-wire";

/**
 * My Tickets: the tickets a fan holds for an event, as the app's wallet and
 * Link in Bio's tickets page show them. This module is the presentation both
 * renderers share, so the app (React Native) and Link in Bio (DOM) say and
 * offer exactly the same things. Engine tickets are drawn from the tickets
 * themselves; a purchase bought on a seller's site is drawn from the read's
 * `collect`, which the server compiles for the viewer (`collectFor`): every
 * sentence, code, document and action, so nothing here knows the seller.
 * It decides nothing about who may see what: the server sends the seller's
 * codes, documents and account box only to their owner.
 */

/** A held ticket as both APIs list it: what an engine ticket shows. A
 *  ticket bought on a seller's site is shown from the read's `collect`. */
export type PurchasedTicket = {
  id: string;
  /** An engine ticket's code; null for one bought on a seller's site. */
  redeemCode?: string | null;
  isWaitlist?: boolean | null;
};

export const PURCHASED_TICKETS_COPY = {
  title: "My Tickets",
  eventLabel: "Event",
  noTickets: "No tickets found",
  loading: "Loading...",
  help: "About your tickets",
  getTheApp: "Get the App",
  waitlistTitle: "WAITLIST",
  waitlistBody: "We'll let you know when your ticket becomes available.",
  venue: "Venue",
  address: "Address",
  doorOpens: "Door Opens",
  showStarts: "Show Starts",
  tba: "TBA",
  previous: "<",
  next: ">",
} as const;

/** "Ticket 2 of 3", under each page of the carousel. */
export function ticketCounter(index: number, count: number) {
  return `Ticket ${index + 1} of ${count}`;
}

/** What an engine ticket's QR code holds (the door's scanner reads it). */
export function engineTicketQrValue(redeemCode: string | null | undefined) {
  return JSON.stringify({ redeemCode });
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * The event's details around the tickets, from its venue-local start
 * (rendered literally; `time: null` hides the clock rows rather than make a
 * time up): "05 October 2026, 20:00" under the title, then venue, address,
 * doors and show.
 */
export function createTicketPageDetails(access: {
  startsAtLocal: { date: string; time: string | null };
  venue?: string | null;
  city?: string | null;
  description?: string | null;
}) {
  const [year, month, day] = access.startsAtLocal.date.split("-");
  const date = `${day} ${MONTHS[Number(month) - 1] ?? ""} ${year}`;
  const time = access.startsAtLocal.time;
  return {
    dateLine: time ? `${date}, ${time}` : date,
    venue:
      access.venue === undefined || access.city === undefined
        ? PURCHASED_TICKETS_COPY.tba
        : `${access.venue || ""}, ${access.city || ""}`,
    address: access.description || PURCHASED_TICKETS_COPY.tba,
    doorOpens: time,
    showStarts: time ? addMinutes(time, 30) : null,
  };
}

function addMinutes(clock: string, minutes: number) {
  const [hours = 0, mins = 0] = clock.split(":").map(Number);
  const total = (((hours * 60 + mins + minutes) % 1440) + 1440) % 1440;
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}


export type VendorTicketsNotice = CollectNotice & {
  testID:
    | "vendor-tickets-pending"
    | "vendor-tickets-processing"
    | "vendor-tickets-with-seller"
    | "vendor-tickets-in-app";
};

export type VendorTicketCode = VendorTicketCodeView;

/** A notice's test id, by its kind. */
function noticeTestId(kind: CollectNotice["kind"]): VendorTicketsNotice["testID"] {
  switch (kind) {
    case "pending":
      return "vendor-tickets-pending";
    case "processing":
      return "vendor-tickets-processing";
    case "with_seller":
      return "vendor-tickets-with-seller";
    case "in_app":
      return "vendor-tickets-in-app";
  }
}

/**
 * My Tickets for a purchase bought on a seller's site: only the tickets, as
 * the server's `collect` says them. While the seller holds an order back,
 * when it will be available; once released, the seller's own codes in a
 * carousel like engine tickets'; otherwise where the tickets are, pointing
 * to the "?". A shared (token) view has no codes.
 */
export function createVendorTicketsModel(collect: Collect) {
  return {
    notices: collect.notices.map(
      (notice): VendorTicketsNotice => ({ ...notice, testID: noticeTestId(notice.kind) }),
    ),
    codes: collect.codes,
  };
}

/** Where "Open …" goes, as `collect` names it: a seller's (or its ticket
 *  app's) app, or a site. */
export type VendorOpenAction = CollectOpen;

/** A ticket document's analytics name, by its kind. */
function documentEventName(kind: Collect["help"]["documents"][number]["kind"]) {
  switch (kind) {
    case "pdf":
      return "ViewTickets.DownloadTicketPdf";
    case "page":
      return "ViewTickets.OpenTicketPage";
  }
}

/** Whether the account box's login shows a password: none for a seller
 *  that signs in by the code it emails. */
function revealsPassword(reveal: NonNullable<Collect["help"]["account"]>["reveal"]) {
  switch (reveal) {
    case "password":
      return true;
    case "emailed_code":
      return false;
  }
}

/** The analytics names of My Tickets' "?" actions, the same for every
 *  seller: each event carries the purchase's listing (`collect.listingId`)
 *  as `listing_id`, never the seller. */
export const VENDOR_TICKETS_EVENTS = {
  help: "ViewTickets.Help",
  open: "ViewTickets.Open",
  selectOrder: "ViewTickets.SelectOrder",
  revealLogin: "ViewTickets.RevealLogin",
  copyEmail: "ViewTickets.CopyEmail",
  copyPassword: "ViewTickets.CopyPassword",
} as const;

/**
 * My Tickets' "?" for a purchase bought on a seller's site, as the server's
 * `collect` says it: how these tickets work, what was bought (the order's
 * lines and the seller's references), the order's ticket documents when the
 * owner has any (a PDF to download, the seller's ticket page), and, for an
 * order Lissen holds in a seller account, that account's box for the owner,
 * with the login fetched only when they ask (identity ADR 0006); otherwise
 * "Open …", the seller's app or site.
 */
export function createVendorTicketsHelpModel(collect: Collect) {
  const { help } = collect;
  return {
    title: PURCHASED_TICKETS_COPY.help,
    subtitle: help.subtitle,
    order: {
      heading: help.order.heading,
      lines: help.order.lines,
      references: help.references.map((reference) => ({
        key: `${reference.label}:${reference.value}`,
        label: reference.label,
        value: reference.value,
      })),
    },
    documents: help.documents.length
      ? {
          heading: "Your tickets",
          items: help.documents.map((document) => ({
            key: `${document.kind}:${document.url}`,
            url: document.url,
            label: document.label,
            eventName: documentEventName(document.kind),
          })),
        }
      : null,
    /** The owner's login box, one login per order, and "Open …". */
    account: help.account
      ? {
          heading: help.account.heading,
          body: help.account.body,
          showsPassword: revealsPassword(help.account.reveal),
          failed: "Your login details couldn’t be loaded. Try again.",
          orders: help.account.orders,
          open: help.account.open,
        }
      : null,
    /** "Open …" for a purchase without an account box. */
    open: help.open,
  };
}

/** "Show login", "Loading…", "Hide login". */
export function vendorLoginButtonLabel(shown: boolean, loading: boolean) {
  return shown ? "Hide login" : loading ? "Loading…" : "Show login";
}

/** An app's store page for the visitor's platform. */
export function vendorAppStoreUrl(
  app: Pick<Extract<CollectOpen, { kind: "app" }>, "appStore" | "playStore">,
  userAgent: string,
) {
  return /Android/i.test(userAgent) ? app.playStore : app.appStore;
}

/** Where "Open …" goes on the web, which cannot open an app's scheme: the
 *  app's store page for the visitor's platform, or the site. */
export function vendorOpenWebUrl(action: VendorOpenAction, userAgent: string) {
  switch (action.kind) {
    case "app":
      return vendorAppStoreUrl(action, userAgent);
    case "site":
      return action.url;
  }
}
